import React from 'react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ChevronDown, Music2, Youtube, Calendar, MapPin } from 'lucide-react';
export function LandingPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const tourDates = [{
    date: 'June 20–21',
    festival: 'TBC',
    venue: 'Liverpool'
  }, {
    date: 'July 3–5',
    festival: 'TBC',
    venue: 'Sussex'
  }, {
    date: 'July 17–26',
    festival: 'TBC',
    venue: 'Various Venues, Edinburgh'
  }, {
    date: 'July 23–26',
    festival: 'TBC',
    venue: 'Wiltshire'
  }];
  return <div className="min-h-screen w-full bg-[#FAF8F5] text-[#1A1A2E] font-sans selection:bg-[#C9A227] selection:text-[#1A1A2E] overflow-x-hidden" data-id="element-145">
      {/* 1. HERO SECTION */}
      <header className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center text-center px-6 bg-[#FAF8F5]" data-id="element-146">
        <div className="relative z-10 flex flex-col items-center space-y-6 md:space-y-8 animate-in fade-in zoom-in duration-1000" data-id="element-147">
          {/* Responsive logo - smaller on mobile */}
          <div className="md:hidden" data-id="element-148">
            <Logo size="lg" layout="vertical" variant="dark" data-id="element-149" />
          </div>
          <div className="hidden md:block" data-id="element-150">
            <Logo size="xl" layout="vertical" variant="dark" data-id="element-151" />
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-light text-[#1A1A2E] tracking-tight max-w-4xl leading-tight mt-4 md:mt-8 px-2" data-id="element-152">
            Africa's finest,{' '}
            <span className="text-[#C9A227] font-normal italic" data-id="element-153">
              on stage across the UK & EU.
            </span>
          </h1>
        </div>

        <button onClick={() => scrollToSection('about')} className="absolute bottom-8 md:bottom-12 z-10 text-[#1A1A2E]/40 hover:text-[#C9A227] transition-colors duration-300 animate-bounce" aria-label="Scroll down" data-id="element-154">
          <ChevronDown size={28} strokeWidth={1} data-id="element-155" />
        </button>
      </header>

      {/* 2. ABOUT SECTION */}
      <section id="about" className="min-h-screen py-16 md:py-32 px-6 max-w-4xl mx-auto text-center flex flex-col items-center justify-center space-y-8 md:space-y-12 relative" data-id="element-156">
        <p className="text-xl sm:text-2xl md:text-4xl font-light leading-relaxed text-[#1A1A2E]" data-id="element-157">
          Boutique booking for the new era. Representing Africa's most
          compelling voices. Curating unforgettable live experiences across the
          UK and EU.
        </p>
        <p className="text-xs sm:text-sm font-medium tracking-[0.2em] md:tracking-[0.3em] uppercase text-[#1A1A2E]/40" data-id="element-158">
          London based. Africa inspired.
        </p>

        <button onClick={() => scrollToSection('artist')} className="absolute bottom-8 md:bottom-12 text-[#1A1A2E]/40 hover:text-[#C9A227] transition-colors duration-300 animate-bounce" aria-label="Scroll to featured artist" data-id="element-159">
          <ChevronDown size={28} strokeWidth={1} data-id="element-160" />
        </button>
      </section>

      {/* 3. FEATURED ARTIST SECTION */}
      <section id="artist" className="bg-white py-16 md:py-32 overflow-hidden" data-id="element-161">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-162">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start lg:items-center" data-id="element-163">
            {/* Image Column */}
            <div className="relative order-2 lg:order-1 mb-16 lg:mb-0" data-id="element-164">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-sm shadow-2xl" data-id="element-165">
                <img src="https://cdn.magicpatterns.com/uploads/gvCoDTDfexpXjhWuRMUuju/selaelo4.jpg" alt="Selaelo Selota with his guitar" className="w-full h-full object-cover object-top" data-id="element-166" />
              </div>
              {/* Floating Quote Card - repositioned for mobile */}
              <div className="relative lg:absolute lg:-bottom-8 lg:-right-12 mt-6 lg:mt-0 bg-[#1A1A2E] text-[#FAF8F5] p-6 md:p-8 max-w-full lg:max-w-sm shadow-xl rounded-sm border-l-4 border-[#C9A227]" data-id="element-167">
                <p className="text-base md:text-lg italic font-light leading-relaxed" data-id="element-168">
                  "He's a beast on stage! His breathtaking energy sets him apart
                  from other South African jazzmen."
                </p>
                <p className="mt-3 md:mt-4 text-[10px] md:text-xs tracking-widest uppercase text-[#C9A227]" data-id="element-169">
                  — Africultures, Nuits d'Afrique
                </p>
              </div>
            </div>

            {/* Content Column */}
            <div className="space-y-6 md:space-y-10 order-1 lg:order-2" data-id="element-170">
              <div className="space-y-2" data-id="element-171">
                <span className="text-[#C9A227] font-medium tracking-[0.15em] md:tracking-[0.2em] uppercase text-xs md:text-sm" data-id="element-172">
                  Featured Artist
                </span>
                <h2 className="text-4xl sm:text-5xl md:text-7xl font-medium text-[#1A1A2E] tracking-tight" data-id="element-173">
                  Selaelo Selota
                </h2>
                <p className="text-lg md:text-xl text-[#1A1A2E]/60 font-light" data-id="element-174">
                  A Pioneer of Southern African Jazz
                </p>
              </div>

              <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#1A1A2E]/80 font-light leading-relaxed" data-id="element-175">
                <p data-id="element-176">
                  For over 25 years, Selaelo Selota has been one of Southern
                  Africa's most celebrated jazz voices—a platinum-selling
                  guitarist, composer, and bandleader whose groove-driven sound
                  sits in the lineage of Hugh Masekela, Mulatu Astatke, and Tony
                  Allen.
                </p>

                <div className="space-y-2" data-id="element-177">
                  <h3 className="text-xs md:text-sm font-medium tracking-[0.15em] md:tracking-[0.2em] uppercase text-[#1A1A2E]" data-id="element-178">
                    The Live Show
                  </h3>
                  <p data-id="element-179">
                    Fronting a six-piece band, Selaelo delivers high-energy,
                    festival-ready sets built on rhythm, call-and-response, and
                    collective joy. A veteran of major stages across Africa,
                    North America, and Europe, his performances are designed for
                    large outdoor crowds and sunny afternoon moments.
                  </p>
                </div>
              </div>

              {/* Career Highlights */}
              <div className="space-y-3 md:space-y-4" data-id="element-180">
                <h3 className="text-xs md:text-sm font-medium tracking-[0.15em] md:tracking-[0.2em] uppercase text-[#1A1A2E]" data-id="element-181">
                  Career Highlights
                </h3>
                <div className="flex flex-wrap gap-2 md:gap-3" data-id="element-182">
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-183">
                    100,000+ Album Sales
                  </Badge>
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-184">
                    2× SAMA Winner
                  </Badge>
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-185">
                    SAMRO Songwriter of the Year
                  </Badge>
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-186">
                    New Orleans Jazz Fest
                  </Badge>
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-187">
                    North Sea Jazz
                  </Badge>
                  <Badge variant="outline" className="border-[#1A1A2E]/20 text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-188">
                    Cape Town Int'l Jazz Festival
                  </Badge>
                  <Badge variant="accent" className="bg-[#C9A227] text-[#1A1A2E] text-[9px] md:text-[10px]" data-id="element-189">
                    Sold-out 25th Anniversary Tour (SA, 2025)
                  </Badge>
                </div>
              </div>

              <p className="text-base md:text-lg text-[#1A1A2E]/80 font-light leading-relaxed italic" data-id="element-190">
                Now bringing his live show to UK & EU audiences, Selaelo offers
                festivals a rare opportunity to present a true African jazz
                original in full flight.
              </p>

              <div className="p-6 md:p-8 bg-[#FAF8F5] border-l-2 border-[#C9A227] rounded-sm" data-id="element-191">
                <p className="text-lg md:text-xl italic text-[#1A1A2E] font-light" data-id="element-192">
                  "My music sounds like home."
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-2 md:pt-4" data-id="element-193">
                <Button variant="primary" size="md" className="bg-[#1A1A2E] hover:bg-[#1A1A2E]/90 w-full sm:w-auto" leftIcon={<Music2 size={16} data-id="element-195" />} data-id="element-194">
                  Listen on Spotify
                </Button>
                <Button variant="secondary" size="md" className="w-full sm:w-auto" leftIcon={<Youtube size={16} data-id="element-197" />} data-id="element-196">
                  Watch on YouTube
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. TOUR SECTION */}
      <section className="py-16 md:py-32 px-4 sm:px-6 bg-[#1A1A2E] text-[#FAF8F5]" data-id="element-198">
        <div className="max-w-5xl mx-auto space-y-10 md:space-y-16" data-id="element-199">
          <div className="text-center space-y-3 md:space-y-4" data-id="element-200">
            <h2 className="text-3xl md:text-5xl font-light tracking-tight" data-id="element-201">
              Summer 2025
            </h2>
            <p className="text-[#C9A227] tracking-[0.15em] md:tracking-[0.2em] uppercase text-xs md:text-sm font-medium" data-id="element-202">
              Selaelo Selota — Live in the UK & EU
            </p>
          </div>

          <div className="space-y-3 md:space-y-0 md:grid md:gap-px md:bg-[#FAF8F5]/10 md:border md:border-[#FAF8F5]/10 md:rounded-sm md:overflow-hidden" data-id="element-203">
            {tourDates.map((tour, index) => <div key={index} className="bg-[#1A1A2E] p-5 md:p-8 rounded-sm md:rounded-none border border-[#FAF8F5]/10 md:border-0 space-y-3 md:space-y-0 md:grid md:grid-cols-3 md:gap-4 md:items-center hover:bg-[#FAF8F5]/5 transition-colors duration-300 group" data-id="element-204">
                <div className="flex items-center gap-3 md:gap-4 text-[#FAF8F5]/60 group-hover:text-[#C9A227] transition-colors" data-id="element-205">
                  <Calendar size={16} className="flex-shrink-0" data-id="element-206" />
                  <span className="font-mono text-xs md:text-sm tracking-wider uppercase" data-id="element-207">
                    {tour.date}
                  </span>
                </div>
                <div className="text-lg md:text-2xl font-medium pl-7 md:pl-0" data-id="element-208">
                  {tour.festival}
                </div>
                <div className="flex items-center gap-2 text-[#FAF8F5]/60 md:justify-end pl-7 md:pl-0" data-id="element-209">
                  <MapPin size={12} className="flex-shrink-0" data-id="element-210" />
                  <span className="text-xs md:text-sm" data-id="element-211">{tour.venue}</span>
                </div>
              </div>)}
          </div>

          <div className="text-center" data-id="element-212">
            <p className="text-[#FAF8F5]/40 text-xs md:text-sm uppercase tracking-widest" data-id="element-213">
              More dates to be announced.
            </p>
          </div>
        </div>
      </section>

      {/* 5. CONTACT SECTION */}
      <section className="py-16 md:py-32 px-6 text-center space-y-8 md:space-y-12" data-id="element-214">
        <div className="space-y-4 md:space-y-6" data-id="element-215">
          <h2 className="text-2xl md:text-4xl font-light text-[#1A1A2E]" data-id="element-216">
            Booking Enquiries
          </h2>
          <a href="mailto:booking@somethinkmusic.co.uk" className="block text-base sm:text-xl md:text-3xl font-medium text-[#C9A227] hover:text-[#1A1A2E] transition-colors duration-300 underline decoration-1 underline-offset-4 md:underline-offset-8 break-all sm:break-normal" data-id="element-217">
            booking@somethinkmusic.co.uk
          </a>
        </div>
      </section>

      {/* 6. FOUNDER SECTION */}
      <section className="py-16 md:py-32 px-6 bg-white" data-id="element-218">
        <div className="max-w-3xl mx-auto text-center space-y-6 md:space-y-8" data-id="element-219">
          <div className="space-y-2" data-id="element-220">
            <h2 className="text-2xl md:text-4xl font-medium text-[#1A1A2E]" data-id="element-221">
              Eddie Boyd
            </h2>
            <p className="text-xs md:text-sm font-medium tracking-[0.15em] md:tracking-[0.2em] uppercase text-[#C9A227]" data-id="element-222">
              Founder, Somethink Music
            </p>
          </div>

          <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#1A1A2E]/80 font-light leading-relaxed" data-id="element-223">
            <p data-id="element-224">
              South African-born, London-based. I spent my formative years
              studying, playing, and booking music back home—then five years at
              Google helping launch YouTube Music across Europe.
            </p>
            <p data-id="element-225">
              Somethink Music was born from a simple desire: to bring the
              beautiful music of South Africa to UK and European stages.
            </p>
          </div>
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer className="bg-[#1A1A2E] text-[#FAF8F5] py-8 md:py-12 px-6 border-t border-[#FAF8F5]/10" data-id="element-226">
        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8 text-center" data-id="element-227">
          <p className="text-[10px] md:text-xs uppercase tracking-widest opacity-60" data-id="element-228">
            © 2026 Somethink Music
          </p>
          <p className="text-[10px] md:text-xs leading-relaxed opacity-40 max-w-2xl mx-auto" data-id="element-229">
            Somethink Music is a trading name of Somethink Ltd, a company
            registered in England and Wales. Company Registration No: 11584106
            Registered Office: Kemp House, 160 City Road, London, EC1V 2NX,
            UNITED KINGDOM
          </p>
        </div>
      </footer>
    </div>;
}